import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { uploadFileToGCS } from '../utils/gcsUpload';
import { Plus, MagnifyingGlass, Download, Trash, Eye, Paperclip, X, Question, EnvelopeSimple, PaperPlaneTilt, PencilSimple } from '@phosphor-icons/react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
  query_number: 120,
  title: 250,
  status: 100,
  discipline: 100,
  from: 140,
  due_date: 100,
  impact: 80,
  actions: 80
};

// Sort icon component (outside main component to avoid re-creation)
const SortIcon = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-3 w-3 text-gray-400 ml-1" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="h-3 w-3 text-blue-600 ml-1" />
    : <ArrowDown className="h-3 w-3 text-blue-600 ml-1" />;
};

const TechnicalQueries = ({ user, selectedProject }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [lateOnly, setLateOnly] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const fileInputRef = useRef(null);

  // Only Document Controllers and System Admins may edit technical queries
  const canEdit = !!user && (
    user.is_super_admin ||
    ['admin', 'system admin', 'document controller'].includes((user.role || '').toLowerCase())
  );
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('query_number');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Resizable columns state
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const resizingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  
  const [newItem, setNewItem] = useState({
    query_number: '',
    title: '',
    sender_reference: '',
    keywords: '',
    content: '',
    response: '',
    status: 'new',
    subclass: '',
    discipline: '',
    query_type: '',
    cost_impact: '',
    schedule_impact: '',
    design_impact: '',
    requested_priority: '',
    assigned_priority: '',
    sent_by_company: '',
    sent_by_department: '',
    sent_by_person: '',
    sent_to_company: '',
    sent_to_department: '',
    sent_to_person: '',
    package_number: '',
    package_revision: '',
    language: '',
    due_date: '',
    received_date: '',
    direction: 'incoming'
  });

  useEffect(() => {
    fetchItems();
  }, [activeTab, searchTerm, filterStatus, filterDiscipline, outstandingOnly, lateOnly, selectedProject]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (activeTab === 'inbox') params.append('direction', 'incoming');
      else if (activeTab === 'outgoing') params.append('direction', 'outgoing');
      
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus) params.append('status', filterStatus);
      if (filterDiscipline) params.append('discipline', filterDiscipline);
      if (outstandingOnly) params.append('outstanding_only', 'true');
      if (lateOnly) params.append('late_only', 'true');
      if (selectedProject) params.append('project_id', selectedProject);
      
      const response = await axios.get(`${API}/queries?${params.toString()}`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch queries');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (item) => {
    if (!canEdit) {
      toast.error('Only Document Controllers and System Admins can edit technical queries');
      return;
    }
    setEditingItemId(item.id);
    setNewItem({
      query_number: item.query_number || '',
      title: item.subject || '',
      sender_reference: item.sender_reference || '',
      keywords: Array.isArray(item.tags) ? item.tags.join(', ') : (item.keywords || ''),
      content: item.description || '',
      response: item.response || '',
      status: item.status || 'new',
      subclass: item.subclass || '',
      discipline: item.discipline || '',
      query_type: item.query_type || '',
      cost_impact: item.cost_impact || '',
      schedule_impact: item.schedule_impact || '',
      design_impact: item.impact_description || '',
      requested_priority: item.priority || '',
      assigned_priority: item.assigned_priority || '',
      sent_by_company: item.from_company || '',
      sent_by_department: item.from_department || '',
      sent_by_person: item.from_contact || '',
      sent_to_company: item.to_company || '',
      sent_to_department: item.to_department || '',
      sent_to_person: item.to_contact || '',
      purchase_order: item.purchase_order || '',
      received_date: item.date_raised ? String(item.date_raised).slice(0, 10) : '',
      due_date: item.date_required ? String(item.date_required).slice(0, 10) : '',
    });
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newItem.query_number || !newItem.title) {
      toast.error('Query Number and Title are required');
      return;
    }
    
    try {
      // Map frontend fields to backend expected fields
      const payload = {
        query_number: newItem.query_number,
        subject: newItem.title,
        query_type: newItem.query_type || 'technical',
        from_company: newItem.sent_by_company,
        from_contact: newItem.sent_by_person,
        to_company: newItem.sent_to_company,
        to_contact: newItem.sent_to_person,
        date_raised: newItem.received_date || null,
        date_required: newItem.due_date || null,
        status: newItem.status || 'new',
        priority: newItem.requested_priority || 'normal',
        discipline: newItem.discipline,
        description: newItem.content,
        cost_impact: newItem.cost_impact,
        schedule_impact: newItem.schedule_impact,
        impact_description: newItem.design_impact,
        tags: newItem.keywords ? newItem.keywords.split(',').map(t => t.trim()) : []
      };
      
      if (editingItemId) {
        await axios.put(`${API}/queries/${editingItemId}`, payload);
        toast.success('Query updated successfully');
      } else {
        await axios.post(`${API}/queries`, payload);
        toast.success('Query created successfully');
      }
      setCreateOpen(false);
      setEditingItemId(null);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('Save query error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || error.message || (editingItemId ? 'Failed to update query' : 'Failed to create query'));
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/queries/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'queries_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Queries exported to Excel');
    } catch (error) {
      toast.error('Failed to export queries');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;
    
    try {
      await axios.delete(`${API}/queries/${id}`);
      toast.success('Query deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete query');
    }
  };

  const handleView = async (item) => {
    setSelectedItem(item);
    setViewOpen(true);
    try {
      const response = await axios.get(`${API}/queries/${item.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      setAttachments([]);
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!selectedItem || !file) return;
    
    setUploading(true);
    try {
      const info = await uploadFileToGCS(file, 'queries');
      await axios.post(`${API}/uploads/register-item-attachment`, {
        item_id: selectedItem.id,
        item_type: 'technical_query',
        gcs_path: info.gcs_path,
        original_filename: info.original_filename,
        file_size: info.file_size,
        content_type: info.content_type,
      });
      toast.success('Attachment uploaded');
      const response = await axios.get(`${API}/queries/${selectedItem.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(`${API}/item-attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`${API}/item-attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      const response = await axios.get(`${API}/queries/${selectedItem.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await axios.put(`${API}/queries/${selectedItem.id}`, { status });
      toast.success('Status updated');
      fetchItems();
      setSelectedItem({ ...selectedItem, status });
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateResponse = async () => {
    if (!selectedItem.response) return;
    try {
      await axios.put(`${API}/queries/${selectedItem.id}`, { response: selectedItem.response });
      toast.success('Response saved');
    } catch (error) {
      toast.error('Failed to save response');
    }
  };

  const resetForm = () => {
    setNewItem({
      query_number: '',
      title: '',
      sender_reference: '',
      keywords: '',
      content: '',
      response: '',
      status: 'new',
      subclass: '',
      discipline: '',
      query_type: '',
      cost_impact: '',
      schedule_impact: '',
      design_impact: '',
      requested_priority: '',
      assigned_priority: '',
      sent_by_company: '',
      sent_by_department: '',
      sent_by_person: '',
      sent_to_company: '',
      sent_to_department: '',
      sent_to_person: '',
      package_number: '',
      package_revision: '',
      language: '',
      due_date: '',
      received_date: '',
      direction: 'incoming'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-orange-100 text-orange-800',
      answered: 'bg-purple-100 text-purple-800',
      closed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(String(dateStr).endsWith('Z') || String(dateStr).includes('+') ? dateStr : dateStr + 'Z').toLocaleDateString();
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sorted items
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      // Handle 'title' column - map to 'subject' from backend
      if (sortColumn === 'title') {
        aVal = a.subject || a.title || '';
        bVal = b.subject || b.title || '';
      }
      
      // Handle 'from' virtual column
      if (sortColumn === 'from') {
        aVal = a.from_company || a.from_contact || a.sent_by_company || a.sent_by_person || '';
        bVal = b.from_company || b.from_contact || b.sent_by_company || b.sent_by_person || '';
      }
      
      // Handle 'due_date' column - map to 'date_required' from backend
      if (sortColumn === 'due_date') {
        aVal = a.date_required || a.due_date || '';
        bVal = b.date_required || b.due_date || '';
      }

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (sortColumn === 'due_date' || sortColumn === 'received_date' || sortColumn === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sortColumn, sortDirection]);

  // Column resize handlers
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = columnKey;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[columnKey] || 150;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const diff = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(60, startWidthRef.current + diff);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current]: newWidth }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Render resizable header
  const renderResizableHeader = (column, label, sortable = true) => (
    <th 
      key={column}
      className="text-left p-3 font-semibold text-gray-700 relative group"
      style={{ width: columnWidths[column], minWidth: columnWidths[column] }}
    >
      {sortable ? (
        <button 
          onClick={() => handleSort(column)} 
          className="flex items-center hover:text-gray-900"
        >
          {label} <SortIcon column={column} sortColumn={sortColumn} sortDirection={sortDirection} />
        </button>
      ) : (
        <span>{label}</span>
      )}
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
        onMouseDown={(e) => startResize(column, e)}
      />
    </th>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="queries-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-1">
            Queries
          </h1>
          <p className="text-gray-500">Manage questions and clarifications</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={handleExportExcel}
            className="rounded-xl h-11 px-4"
          >
            <Download size={18} className="mr-2" />
            Export Excel
          </Button>
          
          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) { setEditingItemId(null); resetForm(); } }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 rounded-xl h-11 px-5" data-testid="create-query-btn">
                <Plus size={18} className="mr-2" />
                New Query
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">{editingItemId ? 'Edit Query' : 'Create New Query'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Query Number*</Label>
                  <Input
                    value={newItem.query_number}
                    onChange={(e) => setNewItem({ ...newItem, query_number: e.target.value })}
                    placeholder="e.g., TQ-2025-001"
                    required
                  />
                </div>
                <div>
                  <Label>Direction*</Label>
                  <Select value={newItem.direction} onValueChange={(val) => setNewItem({ ...newItem, direction: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="incoming">Incoming</SelectItem>
                      <SelectItem value="outgoing">Outgoing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Title*</Label>
                <Input
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  placeholder="Query title"
                  required
                />
              </div>
              
              <div>
                <Label>Content / Question</Label>
                <Textarea
                  value={newItem.content}
                  onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
                  placeholder="Describe the query..."
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={newItem.status} onValueChange={(val) => setNewItem({ ...newItem, status: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="answered">Answered</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Discipline</Label>
                  <Input
                    value={newItem.discipline}
                    onChange={(e) => setNewItem({ ...newItem, discipline: e.target.value })}
                    placeholder="e.g., Mechanical"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Input
                    value={newItem.query_type}
                    onChange={(e) => setNewItem({ ...newItem, query_type: e.target.value })}
                    placeholder="e.g., Clarification"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Cost Impact</Label>
                  <Select value={newItem.cost_impact} onValueChange={(val) => setNewItem({ ...newItem, cost_impact: val })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Schedule Impact</Label>
                  <Select value={newItem.schedule_impact} onValueChange={(val) => setNewItem({ ...newItem, schedule_impact: val })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Design Impact</Label>
                  <Select value={newItem.design_impact} onValueChange={(val) => setNewItem({ ...newItem, design_impact: val })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Keywords</Label>
                  <Input
                    value={newItem.keywords}
                    onChange={(e) => setNewItem({ ...newItem, keywords: e.target.value })}
                    placeholder="e.g., pump, valve, specification"
                  />
                </div>
                <div>
                  <Label>Sender Reference</Label>
                  <Input
                    value={newItem.sender_reference}
                    onChange={(e) => setNewItem({ ...newItem, sender_reference: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Sender Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={newItem.sent_by_company}
                      onChange={(e) => setNewItem({ ...newItem, sent_by_company: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={newItem.sent_by_department}
                      onChange={(e) => setNewItem({ ...newItem, sent_by_department: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Person</Label>
                    <Input
                      value={newItem.sent_by_person}
                      onChange={(e) => setNewItem({ ...newItem, sent_by_person: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Recipient Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={newItem.sent_to_company}
                      onChange={(e) => setNewItem({ ...newItem, sent_to_company: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={newItem.sent_to_department}
                      onChange={(e) => setNewItem({ ...newItem, sent_to_department: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Person</Label>
                    <Input
                      value={newItem.sent_to_person}
                      onChange={(e) => setNewItem({ ...newItem, sent_to_person: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newItem.due_date}
                    onChange={(e) => setNewItem({ ...newItem, due_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Received Date</Label>
                  <Input
                    type="date"
                    value={newItem.received_date}
                    onChange={(e) => setNewItem({ ...newItem, received_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Package Number</Label>
                  <Input
                    value={newItem.package_number}
                    onChange={(e) => setNewItem({ ...newItem, package_number: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Package Revision</Label>
                  <Input
                    value={newItem.package_revision}
                    onChange={(e) => setNewItem({ ...newItem, package_revision: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Language</Label>
                  <Input
                    value={newItem.language}
                    onChange={(e) => setNewItem({ ...newItem, language: e.target.value })}
                    placeholder="e.g., English"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Requested Priority</Label>
                  <Select value={newItem.requested_priority} onValueChange={(val) => setNewItem({ ...newItem, requested_priority: val })}>
                    <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assigned Priority</Label>
                  <Select value={newItem.assigned_priority} onValueChange={(val) => setNewItem({ ...newItem, assigned_priority: val })}>
                    <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button type="submit" className="w-full">{editingItemId ? 'Save Changes' : 'Create Query'}</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="inbox" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <EnvelopeSimple size={16} />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="search" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <MagnifyingGlass size={16} />
            Search
          </TabsTrigger>
          <TabsTrigger value="outgoing" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <PaperPlaneTilt size={16} />
            Outgoing
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 shadow-apple-sm">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by number, title, keywords, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 rounded-xl border-gray-200"
            />
          </div>
          <Select value={filterStatus || "all"} onValueChange={(val) => setFilterStatus(val === "all" ? "" : val)}>
            <SelectTrigger className="w-40 h-10 rounded-xl border-gray-200">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="answered">Answered</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Discipline"
            value={filterDiscipline}
            onChange={(e) => setFilterDiscipline(e.target.value)}
            className="w-40 h-10 rounded-xl border-gray-200"
          />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <Checkbox checked={outstandingOnly} onCheckedChange={setOutstandingOnly} />
            Outstanding only
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <Checkbox checked={lateOnly} onCheckedChange={setLateOnly} />
            Late only
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-apple-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '900px' }}>
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {renderResizableHeader('query_number', 'Query Nr.')}
                {renderResizableHeader('title', 'Title')}
                {renderResizableHeader('status', 'Status')}
                {renderResizableHeader('discipline', 'Discipline')}
                {renderResizableHeader('from', 'From')}
                {renderResizableHeader('due_date', 'Due Date')}
                {renderResizableHeader('impact', 'Impact', false)}
                {renderResizableHeader('actions', 'Actions', false)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-12">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-500">No queries found</td>
                </tr>
              ) : (
                sortedItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-3 font-medium text-gray-900" style={{ width: columnWidths.query_number }}>{item.query_number}</td>
                  <td className="p-3 truncate text-gray-600" style={{ width: columnWidths.title, maxWidth: columnWidths.title }} title={item.subject || item.title}>{item.subject || item.title || '-'}</td>
                  <td className="p-3" style={{ width: columnWidths.status }}>
                    <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${getStatusColor(item.status)}`}>
                      {item.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-3 text-gray-600" style={{ width: columnWidths.discipline }}>{item.discipline || '-'}</td>
                  <td className="p-3 text-gray-600" style={{ width: columnWidths.from }}>{item.from_company || item.from_contact || item.sent_by_company || item.sent_by_person || '-'}</td>
                  <td className="p-3 text-gray-600" style={{ width: columnWidths.due_date }}>{formatDate(item.date_required || item.due_date)}</td>
                  <td className="p-3" style={{ width: columnWidths.impact }}>
                    {(item.cost_impact || item.schedule_impact || item.impact_description || item.design_impact) && (
                      <div className="flex gap-1">
                        {item.cost_impact && item.cost_impact !== 'none' && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-600 rounded-md">$</span>
                        )}
                        {item.schedule_impact && item.schedule_impact !== 'none' && (
                          <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-md">⏱</span>
                        )}
                        {(item.impact_description || item.design_impact) && (item.impact_description !== 'none' && item.design_impact !== 'none') && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-md">✎</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3" style={{ width: columnWidths.actions }}>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleView(item)} title="View" className="h-8 w-8 p-0 rounded-lg hover:bg-gray-100">
                        <Eye size={16} className="text-gray-500" />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => handleStartEdit(item)} title="Edit" className="h-8 w-8 p-0 rounded-lg hover:bg-blue-50" data-testid={`edit-query-${item.id}`}>
                          <PencilSimple size={16} className="text-blue-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} title="Delete" className="h-8 w-8 p-0 rounded-lg hover:bg-red-50">
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Query Details</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Query Number</Label>
                  <p className="font-medium">{selectedItem.query_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-sm ${getStatusColor(selectedItem.status)}`}>
                      {selectedItem.status?.replace('_', ' ')}
                    </span>
                    <Select value={selectedItem.status} onValueChange={handleUpdateStatus}>
                      <SelectTrigger className="w-32 h-7">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="answered">Answered</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Title</Label>
                <p className="font-medium">{selectedItem.title}</p>
              </div>
              
              {selectedItem.content && (
                <div>
                  <Label className="text-muted-foreground">Question / Content</Label>
                  <p className="bg-gray-50 p-3 rounded-sm whitespace-pre-wrap">{selectedItem.content}</p>
                </div>
              )}
              
              <div>
                <Label className="text-muted-foreground">Response</Label>
                <Textarea
                  value={selectedItem.response || ''}
                  onChange={(e) => setSelectedItem({ ...selectedItem, response: e.target.value })}
                  placeholder="Enter response..."
                  rows={4}
                />
                <Button size="sm" onClick={handleUpdateResponse} className="mt-2">Save Response</Button>
              </div>
              
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-muted-foreground">Discipline</Label>
                  <p>{selectedItem.discipline || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p>{selectedItem.query_type || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Keywords</Label>
                  <p>{selectedItem.keywords || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Language</Label>
                  <p>{selectedItem.language || '-'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="border rounded-sm p-3 text-center">
                  <Label className="text-muted-foreground text-xs">Cost Impact</Label>
                  <p className="font-medium capitalize">{selectedItem.cost_impact || 'None'}</p>
                </div>
                <div className="border rounded-sm p-3 text-center">
                  <Label className="text-muted-foreground text-xs">Schedule Impact</Label>
                  <p className="font-medium capitalize">{selectedItem.schedule_impact || 'None'}</p>
                </div>
                <div className="border rounded-sm p-3 text-center">
                  <Label className="text-muted-foreground text-xs">Design Impact</Label>
                  <p className="font-medium capitalize">{selectedItem.design_impact || 'None'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-sm p-3">
                  <h4 className="font-medium mb-2">From</h4>
                  <p className="text-sm">{selectedItem.sent_by_company || '-'}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.sent_by_department}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.sent_by_person}</p>
                </div>
                <div className="border rounded-sm p-3">
                  <h4 className="font-medium mb-2">To</h4>
                  <p className="text-sm">{selectedItem.sent_to_company || '-'}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.sent_to_department}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.sent_to_person}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Due Date</Label>
                  <p>{formatDate(selectedItem.due_date)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Received Date</Label>
                  <p>{formatDate(selectedItem.received_date)}</p>
                </div>
              </div>
              
              {/* Attachments */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Paperclip size={16} />
                    Attachments ({attachments.length})
                  </h4>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleUploadAttachment(e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      <Plus size={14} className="mr-1" />
                      Add Attachment
                    </Button>
                  </div>
                </div>
                
                {attachments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No attachments</p>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-sm">
                        <span className="text-sm truncate flex-1">{att.original_filename}</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadAttachment(att)}>
                            <Download size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteAttachment(att.id)} className="text-red-500">
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TechnicalQueries;
